import { formatSignedAt, readSignatureAnswer } from '@sunfish/lib/signature-questions';
import { Button } from '@sunfish/ui/components/button';
import { Checkbox } from '@sunfish/ui/components/checkbox';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@sunfish/ui/components/form';
import { Input } from '@sunfish/ui/components/input';
import { createContext, useContext, useId, useRef, type ReactNode } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';

const SCRIPT_FONT = "'Brush Script MT', 'Segoe Script', 'Bradley Hand', cursive";

const SignatureReviewOnlyContext = createContext(false);

// Admins see the attendee's signature and can clear it, but never sign for them.
export function SignatureReviewOnly({ children }: { children: ReactNode }) {
	return <SignatureReviewOnlyContext.Provider value={true}>{children}</SignatureReviewOnlyContext.Provider>;
}

function SignedName({ name, signedAt, action }: { name: string; signedAt?: string; action?: ReactNode }) {
	return (
		<div className="rounded-md border border-dashed border-input px-4 py-3">
			<p className="text-3xl leading-tight" style={{ fontFamily: SCRIPT_FONT }}>
				{name}
			</p>
			{(signedAt || action) && (
				<div className="mt-1 flex flex-wrap items-center justify-between gap-2">
					{signedAt && <p className="text-xs text-muted-foreground">Signed {formatSignedAt(signedAt)}</p>}
					{action}
				</div>
			)}
		</div>
	);
}

export function SignatureAnswerBlock<T extends FieldValues>({
	control,
	name,
	label,
	description,
	required,
	disabled
}: {
	control: Control<T, unknown>;
	name: Path<T>;
	label?: string | null;
	description?: ReactNode;
	required?: boolean;
	disabled?: boolean;
}) {
	const agreeId = useId();
	const reviewOnly = useContext(SignatureReviewOnlyContext);
	const recorded = useRef<{ name: string; signedAt: string } | null>(null);

	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => {
				const answer = readSignatureAnswer(field.value);
				if (answer?.signedAt && !recorded.current) {
					recorded.current = { name: answer.name, signedAt: answer.signedAt };
				}
				const typedName = answer?.name ?? '';
				const stillRecorded = !!recorded.current && recorded.current.name === typedName.trim();
				const agreed = stillRecorded || answer?.agreed === true;

				const setName = (next: string) => {
					if (!next.trim()) return field.onChange(null);
					if (recorded.current && recorded.current.name === next.trim()) return field.onChange(recorded.current);
					field.onChange({ name: next, agreed: false });
				};

				const reviewBody = () => {
					if (!recorded.current) {
						return <p className="text-sm text-muted-foreground">Not signed yet. Only the attendee can sign.</p>;
					}
					if (!answer) {
						return (
							<div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed border-input px-4 py-3">
								<p className="text-sm text-muted-foreground">The signature will be removed when you save.</p>
								<Button type="button" variant="ghost" size="sm" onClick={() => field.onChange(recorded.current)}>
									Undo
								</Button>
							</div>
						);
					}
					return (
						<SignedName
							name={recorded.current.name}
							signedAt={recorded.current.signedAt}
							action={
								!disabled && (
									<Button type="button" variant="ghost" size="sm" onClick={() => field.onChange(null)}>
										Clear signature
									</Button>
								)
							}
						/>
					);
				};

				return (
					<FormItem>
						<div className="flex flex-wrap items-center justify-between">
							{!!label?.length && (
								<FormLabel className="flex flex-wrap gap-1">
									<span>{label}</span>
									{required && <span className="align-super text-destructive">*</span>}
								</FormLabel>
							)}
							<FormMessage className="max-w-[250px] text-right text-xs leading-none" />
						</div>
						{description && <FormDescription>{description}</FormDescription>}
						{reviewOnly ? (
							reviewBody()
						) : (
							<>
								<FormControl>
									<Input
										value={typedName}
										onChange={(event) => setName(event.target.value)}
										onBlur={field.onBlur}
										placeholder="Type your full name"
										autoComplete="name"
										disabled={disabled}
									/>
								</FormControl>
								{typedName.trim() && (
									<SignedName
										name={typedName.trim()}
										signedAt={stillRecorded ? recorded.current?.signedAt : undefined}
									/>
								)}
								{!stillRecorded && (
									<div className="flex items-start gap-2">
										<Checkbox
											id={agreeId}
											checked={agreed}
											disabled={disabled || !typedName.trim()}
											onCheckedChange={(checked) => field.onChange({ name: typedName, agreed: checked === true })}
										/>
										<label htmlFor={agreeId} className="text-sm leading-snug">
											I agree that typing my name above is my electronic signature.
										</label>
									</div>
								)}
							</>
						)}
					</FormItem>
				);
			}}
		/>
	);
}
